import { useEffect, useRef, useState, type ButtonHTMLAttributes } from "react";
import styles from "./PlotCard.module.css";
import { cx } from "../cx";
import { IconChatFill } from "../icons/Icon";

export type PlotCardProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> & {
  coverUrl: string;
  name: string;
  tagline: string;
  /** 총 메시지 수(AC0-266). 카드에는 숫자만 보이고(시안 41:646), 읽어 주는 문구는 앱이 aria-label 로 준다. */
  totalMessageCount: number;
  /** 최대 2개만 보여준다. */
  tags?: string[];
  /** 기본 lazy. 앱 홈은 eager — iOS 스와이프 뒤로가기로 홈이 다시 마운트될 때 lazy 이미지가 한 번 비어 깜빡인다. */
  imageLoading?: "lazy" | "eager";
  /**
   * 움직이는 원본(AC0-327). coverUrl 이 그 첫 프레임 정지본일 때 준다. 카드가 화면에 들어오면 받아서, 다 받은 뒤 정지본 위에 겹친다 —
   * 받는 동안·실패 시에도 정지본이 보여 빈 칸이 없다. 움직임 줄이기 설정이면 받지 않는다.
   */
  motionUrl?: string;
};

/** 이 화면 세션에서 이미 받은 원본. 다시 마운트될 때(iOS 스와이프 뒤로가기 등) 관찰을 기다리지 않고 바로 겹친다 */
const loadedMotion = new Set<string>();
const reducedMotion = () => typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;

const MAX_TAGS = 2;

/** 홈 카드(Figma PlotCard 41:646, AC0-93): 전면 이미지(173:290) + 하단 그라데이션 스크림 위에 이름·한 줄 소개·대화 수 필·태그 2. */
export function PlotCard({ coverUrl, name, tagline, totalMessageCount, tags = [], imageLoading = "lazy", motionUrl, className, ...rest }: PlotCardProps) {
  const root = useRef<HTMLButtonElement>(null);
  // idle: 아직 화면 밖 · loading: 받는 중(투명) · shown: 정지본 위에 보임 · failed: 정지본만
  const [motion, setMotion] = useState<"idle" | "loading" | "shown" | "failed">(() => (motionUrl && loadedMotion.has(motionUrl) ? "shown" : "idle"));

  useEffect(() => {
    setMotion(motionUrl && loadedMotion.has(motionUrl) ? "shown" : "idle");
    const el = root.current;
    if (!motionUrl || loadedMotion.has(motionUrl) || !el || typeof IntersectionObserver === "undefined" || reducedMotion()) return;
    const io = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      io.disconnect();
      setMotion("loading");
    });
    io.observe(el);
    return () => io.disconnect();
  }, [motionUrl]);

  return (
    <button ref={root} type="button" className={cx(styles.root, className)} {...rest}>
      {/* 이미지 실패 시 숨겨서 배경(placeholder)만 남긴다. */}
      <img src={coverUrl} alt="" loading={imageLoading} className={styles.image} onError={(e) => (e.currentTarget.style.visibility = "hidden")} />
      {motionUrl && (motion === "loading" || motion === "shown") && (
        <img
          src={motionUrl}
          alt=""
          className={cx(styles.image, styles.motion, motion === "shown" && styles.motionShown)}
          data-testid="plot-card-motion"
          onLoad={() => {
            loadedMotion.add(motionUrl);
            setMotion("shown");
          }}
          onError={() => setMotion("failed")}
        />
      )}
      <span className={styles.scrim} aria-hidden="true" />
      <span className={styles.info}>
        <span className={styles.name}>{name}</span>
        <span className={styles.tagline}>{tagline}</span>
        <span className={styles.meta}>
          <span className={styles.count}>
            <IconChatFill size={12} />
            <span>{totalMessageCount.toLocaleString("ko-KR")}</span>
          </span>
          {tags.length > 0 && (
            <span className={styles.tags}>
              {tags.slice(0, MAX_TAGS).map((tag) => (
                <span key={tag}>#{tag}</span>
              ))}
            </span>
          )}
        </span>
      </span>
    </button>
  );
}
